import React, { useEffect, useState } from 'react';
import { useApp } from '../context/AppContext';
import { motion } from 'framer-motion';
import { Mail, Phone, MapPin } from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

// Helper to get full image URL from relative path
const getImageUrl = (url) => {
  if (!url) return '';
  // If it's already a full URL, return as is (for backwards compatibility)
  if (url.startsWith('http')) return url;
  // If it's a relative path, prepend BACKEND_URL
  return `${BACKEND_URL}${url}`;
};

export const ContactPage = () => {
  const { t, language } = useApp();
  const [settings, setSettings] = useState(null);
  const [globalSettings, setGlobalSettings] = useState(null);
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    subject: '',
    message: '',
    captcha_answer: '',
  });
  const [phonePrefix, setPhonePrefix] = useState('+40');
  const [customPrefix, setCustomPrefix] = useState('');
  const [showCustomPrefix, setShowCustomPrefix] = useState(false);
  
  const countryPrefixes = [
    { code: '+40', country: '🇷🇴 Romania' },
    { code: '+33', country: '🇫🇷 France' },
    { code: '+34', country: '🇪🇸 Spain' },
    { code: '+39', country: '🇮🇹 Italy' },
    { code: '+49', country: '🇩🇪 Germany' },
    { code: '+44', country: '🇬🇧 UK' },
    { code: '+43', country: '🇦🇹 Austria' },
    { code: '+32', country: '🇧🇪 Belgium' },
    { code: '+31', country: '🇳🇱 Netherlands' },
    { code: '+41', country: '🇨🇭 Switzerland' },
    { code: '+1', country: '🇺🇸 USA/Canada' },
    { code: '+373', country: '🇲🇩 Moldova' },
    { code: '+380', country: '🇺🇦 Ukraine' },
    { code: 'other', country: '🌍 Other...' },
  ];

  const handlePrefixChange = (value) => {
    if (value === 'other') {
      setShowCustomPrefix(true);
      setPhonePrefix('');
    } else {
      setShowCustomPrefix(false);
      setPhonePrefix(value);
      setCustomPrefix('');
    }
  };

  const getActivePrefix = () => {
    return showCustomPrefix ? customPrefix : phonePrefix;
  };

  useEffect(() => {
    fetchSettings();
    fetchGlobalSettings();
  }, []);

  const fetchSettings = async () => {
    try {
      const { data } = await axios.get(`${BACKEND_URL}/api/settings/contact`);
      setSettings(data);
    } catch (error) {
      console.error('Failed to fetch contact settings');
    }
  };

  const fetchGlobalSettings = async () => {
    try {
      const { data } = await axios.get(`${BACKEND_URL}/api/settings/global`);
      setGlobalSettings(data);
    } catch (error) {
      console.error('Failed to fetch global settings');
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      await axios.post(`${BACKEND_URL}/api/contact`, {
        ...formData,
        phone: getActivePrefix() + formData.phone,
        captcha_answer: parseInt(formData.captcha_answer),
      });
      toast.success('Message sent successfully!');
      setFormData({ name: '', email: '', phone: '', subject: '', message: '', captcha_answer: '' });
      setShowCustomPrefix(false);
      setCustomPrefix('');
      setPhonePrefix('+40');
    } catch (error) {
      toast.error('Failed to send message. Check captcha answer.');
    }
  };

  const getTranslatedText = (textObj) => {
    return textObj?.[language] || textObj?.en || '';
  };

  return (
    <div data-testid="contact-page" className="min-h-screen pt-32 pb-24">
      <div className="container mx-auto px-6">
        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} className="mb-12">
          <h1 className="text-4xl md:text-5xl font-serif tracking-tight mb-4">{t('contact_us')}</h1>
          <p className="text-lg text-muted-foreground">{t('visit_note')}</p>
        </motion.div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12">
          {/* Contact Info */}
          <motion.div initial={{ opacity: 0, x: -20 }} animate={{ opacity: 1, x: 0 }} transition={{ delay: 0.1 }}>
            {/* Contact Image */}
            {settings?.contact_image_url && (
              <div className="mb-8">
                <img 
                  src={getImageUrl(settings.contact_image_url)} 
                  alt="Contact" 
                  className="w-full h-64 object-cover rounded-lg"
                />
              </div>
            )}

            <div className="space-y-8">
              {settings?.email && (
                <div className="flex items-start gap-4">
                  <div className="p-3 bg-primary/10 border border-primary/20">
                    <Mail className="text-primary" size={24} />
                  </div>
                  <div>
                    <h3 className="text-xs uppercase tracking-[0.2em] font-bold text-primary mb-2">Email</h3>
                    <a href={`mailto:${settings.email}`} className="text-foreground hover:text-primary transition-colors">
                      {settings.email}
                    </a>
                  </div>
                </div>
              )}

              {settings?.phone && (
                <div className="flex items-start gap-4">
                  <div className="p-3 bg-primary/10 border border-primary/20">
                    <Phone className="text-primary" size={24} />
                  </div>
                  <div>
                    <h3 className="text-xs uppercase tracking-[0.2em] font-bold text-primary mb-2">
                      {language === 'ro' ? 'Telefon' : language === 'fr' ? 'Téléphone' : 'Phone'}
                    </h3>
                    <a href={`tel:${settings.phone}`} className="text-foreground hover:text-primary transition-colors">
                      {settings.phone}
                    </a>
                  </div>
                </div>
              )}

              {/* WhatsApp Button */}
              {globalSettings?.whatsapp_number && globalSettings?.enable_whatsapp && (
                <div className="flex items-start gap-4">
                  <div className="p-3 bg-green-500/20 border border-green-500/30">
                    <svg className="text-green-500" width="24" height="24" viewBox="0 0 24 24" fill="currentColor">
                      <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"/>
                    </svg>
                  </div>
                  <div>
                    <h3 className="text-xs uppercase tracking-[0.2em] font-bold text-green-500 mb-2">WhatsApp</h3>
                    <a 
                      href={`https://wa.me/${globalSettings.whatsapp_number.replace(/[^0-9]/g, '')}`}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="inline-flex items-center gap-2 bg-green-500 hover:bg-green-600 text-white px-4 py-2 rounded-full text-sm font-medium transition-colors"
                    >
                      <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor">
                        <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"/>
                      </svg>
                      {language === 'ro' ? 'Scrie-ne pe WhatsApp' : language === 'fr' ? 'Écrivez-nous sur WhatsApp' : 'Message us on WhatsApp'}
                    </a>
                  </div>
                </div>
              )}

              {settings?.address && (
                <div className="flex items-start gap-4">
                  <div className="p-3 bg-primary/10 border border-primary/20">
                    <MapPin className="text-primary" size={24} />
                  </div>
                  <div>
                    <h3 className="text-xs uppercase tracking-[0.2em] font-bold text-primary mb-2">
                      {language === 'ro' ? 'Adresă' : language === 'fr' ? 'Adresse' : 'Address'}
                    </h3>
                    <p className="text-foreground">{getTranslatedText(settings.address)}</p>
                  </div>
                </div>
              )}
            </div>

            {/* Map */}
            {settings?.map_embed_url && (
              <div className="mt-8">
                <h3 className="text-xs uppercase tracking-[0.2em] font-bold text-primary mb-4">
                  {language === 'ro' ? 'Locație' : language === 'fr' ? 'Emplacement' : 'Location'}
                </h3>
                <iframe
                  src={settings.map_embed_url}
                  width="100%"
                  height="300"
                  style={{ border: 0 }}
                  allowFullScreen=""
                  loading="lazy"
                  title="Location Map"
                  className="rounded-lg"
                />
              </div>
            )}
          </motion.div>

          {/* Contact Form */}
          <motion.div initial={{ opacity: 0, x: 20 }} animate={{ opacity: 1, x: 0 }} transition={{ delay: 0.2 }} className="glass p-8">
            <form onSubmit={handleSubmit} data-testid="contact-form" className="space-y-6">
              <div>
                <input
                  type="text"
                  data-testid="contact-name"
                  placeholder={t('name')}
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  required
                  className="w-full bg-transparent border-b border-white/20 py-3 text-sm focus:outline-none focus:border-primary transition-colors placeholder:text-white/30"
                />
              </div>
              <div>
                <input
                  type="email"
                  data-testid="contact-email"
                  placeholder={t('email')}
                  value={formData.email}
                  onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                  required
                  className="w-full bg-transparent border-b border-white/20 py-3 text-sm focus:outline-none focus:border-primary transition-colors placeholder:text-white/30"
                />
              </div>
              <div>
                <label className="text-xs text-muted-foreground mb-1 block">
                  {language === 'ro' ? 'Telefon' : language === 'fr' ? 'Téléphone' : 'Phone'}
                </label>
                <div className="flex items-center gap-2">
                  {!showCustomPrefix ? (
                    <select
                      value={phonePrefix}
                      onChange={(e) => handlePrefixChange(e.target.value)}
                      className="bg-slate-900 text-sm text-primary font-medium px-2 py-3 border-b border-primary/30 focus:outline-none focus:border-primary cursor-pointer"
                    >
                      {countryPrefixes.map((prefix) => (
                        <option key={prefix.code} value={prefix.code}>
                          {prefix.code === 'other' ? prefix.country : `${prefix.code} ${prefix.country}`}
                        </option>
                      ))}
                    </select>
                  ) : (
                    <div className="flex items-center gap-1">
                      <input
                        type="text"
                        placeholder="+XX"
                        value={customPrefix}
                        onChange={(e) => setCustomPrefix(e.target.value.replace(/[^0-9+]/g, ''))}
                        className="w-20 bg-slate-900 text-sm text-primary font-medium px-2 py-3 border-b border-primary/30 focus:outline-none focus:border-primary"
                        maxLength={5}
                      />
                      <button
                        type="button"
                        onClick={() => handlePrefixChange('+40')}
                        className="text-xs text-muted-foreground hover:text-primary px-2"
                        title="Back to list"
                      >
                        ✕
                      </button>
                    </div>
                  )}
                  <input
                    type="tel"
                    data-testid="contact-phone"
                    placeholder="755 123 456"
                    value={formData.phone}
                    onChange={(e) => setFormData({ ...formData, phone: e.target.value.replace(/[^0-9\s]/g, '') })}
                    className="flex-1 bg-transparent border-b border-white/20 py-3 text-sm focus:outline-none focus:border-primary transition-colors placeholder:text-white/30"
                  />
                </div>
              </div>
              <div>
                <input
                  type="text"
                  data-testid="contact-subject"
                  placeholder={t('subject')}
                  value={formData.subject}
                  onChange={(e) => setFormData({ ...formData, subject: e.target.value })}
                  required
                  className="w-full bg-transparent border-b border-white/20 py-3 text-sm focus:outline-none focus:border-primary transition-colors placeholder:text-white/30"
                />
              </div>
              <div>
                <textarea
                  data-testid="contact-message"
                  placeholder={t('message')}
                  value={formData.message}
                  onChange={(e) => setFormData({ ...formData, message: e.target.value })}
                  required
                  rows={6}
                  className="w-full bg-transparent border-b border-white/20 py-3 text-sm focus:outline-none focus:border-primary transition-colors placeholder:text-white/30 resize-none"
                />
              </div>
              <div>
                <label className="text-xs text-muted-foreground mb-2 block">8 + 6 = ?</label>
                <input
                  type="number"
                  data-testid="contact-captcha"
                  placeholder="Answer"
                  value={formData.captcha_answer}
                  onChange={(e) => setFormData({ ...formData, captcha_answer: e.target.value })}
                  required
                  className="w-full bg-transparent border-b border-white/20 py-3 text-sm focus:outline-none focus:border-primary transition-colors placeholder:text-white/30"
                />
              </div>
              <button
                type="submit"
                data-testid="contact-submit"
                className="w-full bg-primary text-primary-foreground hover:bg-primary/90 px-6 py-4 font-serif text-sm uppercase tracking-wider transition-all"
              >
                {t('send_message')}
              </button>
            </form>
          </motion.div>
        </div>
      </div>
    </div>
  );
};

export default ContactPage;